import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { hasRight } from 'nallian-shared-ui/lib/lib/routesHelper';
import { GetRights } from 'nallian-shared-ui/lib/selectors';
import { Flex, Header, Button, ConfirmModal } from 'nallian-shared-ui/lib/components';
import OrganisationLogoForm from './OrganisationLogoForm';
import RemoveOrganisationLogoService from '../../services/logo/removeOrganisationLogoService';

const OrgLogo = styled.img`
  max-height: 150px;
  object-fit: contain;
  object-position: center;
  width: auto;
  margin: 0 auto;
`;

const ToggleButton = styled(Button)`
  font-size: 1.2rem;
`;

const RemoveButton = styled(Button)`
  font-size: 1.2rem;
  color: ${props => props.theme.stateError} !important;
`;

const ManageOrganisationLogo = ({
  organisation,
  dispatchRemoveOrganisationLogoDelete,
  removeOrganisationLogoFetch,
}) => {
  const { t } = useTranslation();
  const [editMode, setEditMode] = useState(false);

  const rights = useSelector(GetRights);

  const canUpdate = useMemo(() => hasRight(rights, 'organisations/logo/update'), [rights]);
  const canDelete = useMemo(() => hasRight(rights, 'organisations/logo/delete'), [rights]);

  const logoUrl = organisation?.logoUrl;

  useEffect(() => {
    if (logoUrl) setEditMode(false);
  }, [setEditMode, logoUrl]);

  const toggleEditMode = useCallback(() => setEditMode(prev => !prev), [setEditMode]);

  const handleDeleteOrganisationLogo = useCallback(
    () =>
      dispatchRemoveOrganisationLogoDelete(
        organisation.id,
        organisation,
        () => setEditMode(false),
        t
      ),
    [dispatchRemoveOrganisationLogoDelete, setEditMode, organisation, t]
  );

  return (
    <Flex fluid direction="column">
      <Flex
        fluid
        justifyContent="space-between"
        alignItems="center"
        margin={logoUrl ? '0' : '0 0 1.5rem'}
      >
        {(logoUrl || (canUpdate && canDelete)) && <Header>{t('logo')}</Header>}
        {logoUrl && canUpdate && (
          <Flex fluid justifyContent="flex-end" alignItems="center" margin="0 1.2rem 0 0.5rem">
            <ToggleButton
              linkbutton
              onClick={toggleEditMode}
              content={editMode ? t('viewLogo') : t('uploadANewLogo')}
              icon="exchange"
            />
            {logoUrl && canDelete && (
              <>
                <Flex margin="0 0.5rem 0 0">/</Flex>
                <ConfirmModal
                  header={t('areYouSureYouWantToRemoveLogo')}
                  cancelContent={t('cancel')}
                  confirmContent={t('remove')}
                  confirmColor="red"
                  onConfirm={handleDeleteOrganisationLogo}
                  trigger={
                    <RemoveButton
                      linkbutton
                      loading={removeOrganisationLogoFetch.pending}
                      disabled={removeOrganisationLogoFetch.pending}
                      content={t('removeLogo')}
                      icon="trash"
                    />
                  }
                />
              </>
            )}
          </Flex>
        )}
      </Flex>
      <Flex fluid direction="column">
        {logoUrl ? (
          <Flex fluid direction="column">
            {editMode && canUpdate ? (
              <OrganisationLogoForm organisation={organisation} />
            ) : (
              <OrgLogo src={logoUrl} alt="Organisation Logo" />
            )}
          </Flex>
        ) : (
          <Flex fluid direction="column">
            {canUpdate ? (
              <OrganisationLogoForm organisation={organisation} />
            ) : (
              <>{canUpdate && canDelete && <i>{t('noLogoAvailable')}</i>}</>
            )}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

ManageOrganisationLogo.propTypes = {
  dispatchRemoveOrganisationLogoDelete: PropTypes.func.isRequired,
  removeOrganisationLogoFetch: PropTypes.func.isRequired,
  logoUrl: PropTypes.string.isRequired,
  organisation: PropTypes.object,
};

ManageOrganisationLogo.defaultProps = {
  organisation: {},
};

export default RemoveOrganisationLogoService(ManageOrganisationLogo);
